import {Channels, ListingsLinkBody, productApi} from 'cl-api';
import * as _ from 'lodash';
import {store} from '../../../App/store';
import {updateRows, setAdditionalData, ColumnsType} from 'cl-table/redux/actions';
import {FormSelectOptions} from 'cl-form/utils/orderByUtils';
import {updateListings as updateListingsAction} from '../../../MyAccount/ViewDesigns/redux/modules/listingsPage';
import {MappingType} from '../../components/Dialogs/EditColumnDialogs/EditColumnDialog';
import {getProductSchemaInfo} from '../../../MyAccount/Inventory/utils';
import {LinkToProductFormData} from '../../components/ProductLink/LinkToProductDialog';

export interface MappableProductProperty {
  displayName?: string;
  name: string;
  mappingType?: MappingType;
  valueMapping?: {};
}

export const EMPTY_VALUE = 'Empty value';

export function valueToFieldName(productValue) {
  return 'field-' + _.toString(productValue || EMPTY_VALUE).replace('.', '-');
}

export function mappingToValue(mapping: MappableProductProperty, product, options: FormSelectOptions) {
  if (mapping) {
    switch (mapping.mappingType) {
      case MappingType.DirectMapping:
        return product[mapping.name];
      case MappingType.Single:
        return mapping.name;
      case MappingType.ValueMapping:
        const value = product[mapping.name];
        if (mapping.valueMapping) {
          const mappedValue = mapping.valueMapping[valueToFieldName(value)];
          if (mappedValue) {
            return mappedValue;
          }
        }
        if (options && value) {
          const option = _.find(
            options,
            option => _.isString(option.label) && _.toLower(option.label) === _.toLower(value)
          );
          if (option) {
            return option.value;
          }
        }
        return null;
      case MappingType.Empty:
        return null;
    }
  }
  return null;
}

export function updateListingsFromProducts(
  propertyName,
  mappableProductProperty: MappableProductProperty | null,
  tableName: string,
  options?: FormSelectOptions
) {
  const {valueMapping, mappingType} = mappableProductProperty || ({} as MappableProductProperty);
  store.dispatch(
    updateRows({
      name: tableName,
      columnName: propertyName,
      updater: ({_source}) => {
        return mappingToValue(mappableProductProperty, _source, options);
      },
    })
  );
  store.dispatch(
    setAdditionalData({
      name: tableName,
      key: propertyName,
      value: mappableProductProperty ? {...mappableProductProperty, mappingType, valueMapping} : null,
    })
  );
}

const performActionAndUpdateListings = (
  func: typeof productApi.setListingsLink | typeof productApi.createFromListings
) => async (objects: ListingsLinkBody['objects']) => {
  const {updatedListings} = await func({objects});
  const ebayListings = updatedListings[Channels.EBay];
  _.forEach(ebayListings, listing => {
    store.dispatch(updateListingsAction({[listing.itemID]: listing}));
  });
};

export const setListingsLink = performActionAndUpdateListings(productApi.setListingsLink.bind(productApi));
export const createFromListings = performActionAndUpdateListings(productApi.createFromListings.bind(productApi));

export async function getImportProductsCSVDestinationAttributes() {
  const destinationAttributes: ColumnsType = _.pickBy(
    (await getProductSchemaInfo()).columns as any,
    ({dynamic, type, displayType}) => !(dynamic || type === 'join' || displayType === 'table')
  );
  destinationAttributes.Images = {type: 'images'};
  destinationAttributes.ParentSKU = {displayName: 'Parent SKU'};
  return destinationAttributes;
}

export const getVariationsLinkObjects = (formValues: LinkToProductFormData, variations: any[] = null) => {
  const variationsValues = _.omit(formValues, ['sellerId', 'SKU', 'listingSKU', 'channel', 'listingId']);
  if (!_.isEmpty(variationsValues)) {
    return _.map(variationsValues, (SKU: string, variationId: string) => ({
      sellerId: formValues.sellerId,
      SKU,
      listingSKU: variations && variations[variationId] ? variations[variationId].SKU : null,
      channel: formValues.channel,
      listingId: variationId,
    }));
  }
  return [];
};

export const getAttributeIds = (selection, data) => {
  return !_.isUndefined(selection.exclude)
    ? data
        .map(({name}) => name)
        .valueSeq()
        .toArray()
    : undefined;
};
